import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Date the legal text was last reviewed. Kept as a plain ISO string so it can
// be formatted in the user's locale at render time.
const LAST_UPDATED_ISO = '2026-05-27';

const styles = {
  page: {
    minHeight: '100dvh',
    background: '#0F172A',
    color: '#F8FAFC',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    padding: '3rem 1.5rem',
  },
  container: {
    maxWidth: 720,
    margin: '0 auto',
  },
  backLink: {
    color: '#60A5FA',
    textDecoration: 'none',
    fontSize: '0.9rem',
    display: 'inline-block',
    marginBottom: '2rem',
  },
  h1: {
    fontSize: '2rem',
    fontWeight: 800,
    color: '#F8FAFC',
    marginBottom: '0.5rem',
  },
  updated: {
    color: '#94A3B8',
    fontSize: '0.85rem',
    marginBottom: '2.5rem',
  },
  h2: {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: '#F8FAFC',
    marginTop: '2rem',
    marginBottom: '0.75rem',
  },
  h3: {
    fontSize: '1.05rem',
    fontWeight: 600,
    color: '#E2E8F0',
    marginTop: '1.25rem',
    marginBottom: '0.5rem',
  },
  p: {
    color: '#CBD5E1',
    lineHeight: 1.7,
    marginBottom: '1rem',
    fontSize: '0.95rem',
  },
  ul: {
    color: '#CBD5E1',
    lineHeight: 1.7,
    paddingLeft: '1.5rem',
    marginBottom: '1rem',
    fontSize: '0.95rem',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: '1.5rem',
    fontSize: '0.9rem',
  },
  th: {
    textAlign: 'left',
    padding: '0.6rem 1rem',
    borderBottom: '1px solid #334155',
    color: '#F8FAFC',
    fontWeight: 600,
  },
  td: {
    padding: '0.6rem 1rem',
    borderBottom: '1px solid #1E293B',
    color: '#CBD5E1',
  },
  link: {
    color: '#60A5FA',
    textDecoration: 'none',
  },
};

export default function PrivacyPage() {
  const { t, i18n } = useTranslation();
  const lastUpdated = new Date(LAST_UPDATED_ISO).toLocaleDateString(i18n.language, {
    year: 'numeric', month: 'long', day: 'numeric',
  });

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <Link to="/" style={styles.backLink}>{t('legal.back')}</Link>

        <h1 style={styles.h1}>{t('legal.privacy.title')}</h1>
        <p style={styles.updated}>{t('legal.lastUpdated', { date: lastUpdated })}</p>

        {/* The legal body below is intentionally English-only. Translating
            a privacy policy requires per-jurisdiction legal review; until
            that's commissioned, the body is not wrapped in t(). */}

        <p style={styles.p}>
          vQueue ("we", "us", "our") respects your privacy. This Privacy Policy explains what
          data we collect, how we use it, and your rights under the General Data Protection
          Regulation (GDPR) and other applicable privacy laws.
        </p>

        <h2 style={styles.h2}>1. Data We Collect</h2>

        <h3 style={styles.h3}>Customers (End Users joining a queue)</h3>
        <p style={styles.p}>
          When you join a queue, we collect only your first name and an optional emoji avatar.
          This data is stored temporarily in Redis (in-memory) for the duration of your queue
          session and is not persisted to any database. Once your session ends, this data is
          removed automatically. We do not collect phone numbers, email addresses, or any other
          contact information from customers.
        </p>
        <p style={styles.p}>
          Walk-up tickets issued at a physical kiosk are fully anonymous: no name, avatar, or
          other identifier is collected — only a sequential ticket number for the day.
        </p>

        <h3 style={styles.h3}>Push notification subscribers</h3>
        <p style={styles.p}>
          If you opt in to push notifications, we store your browser push subscription endpoint
          and encryption keys, linked to your current ticket. These are deleted when your queue
          session ends, when you cancel your ticket, or when the browser reports the subscription
          as expired.
        </p>

        <h3 style={styles.h3}>Business users (B2B accounts)</h3>
        <p style={styles.p}>Account sign-up requires an email address only. A verification link
        is sent by email to activate the account, after which you set a password. We subsequently
        collect:</p>
        <ul style={styles.ul}>
          <li>Email address (for login, account recovery, and service communication)</li>
          <li>Password (hashed with bcrypt; we never store or see your plaintext password)</li>
          <li>Office (shop), service, and queue configuration you create</li>
          <li>Names and email addresses of staff members you invite to your workspace</li>
          <li>Payment information (processed by Stripe; we do not store card details)</li>
        </ul>

        <h3 style={styles.h3}>Login and session logs</h3>
        <p style={styles.p}>
          Each time a business user signs in, we record the login timestamp, logout timestamp,
          IP address, and browser user-agent string in a <code>login_sessions</code> table. This
          audit trail is visible to the account owner, workspace administrators, and platform
          operators for security purposes. It is retained while the account is active.
        </p>

        <h3 style={styles.h3}>Administrative actions</h3>
        <p style={styles.p}>
          Privileged actions taken by platform operators or workspace administrators (for example
          freezing a tenant, revoking an invite, or changing a plan) are written to an internal
          audit log recording the actor, action, target, and timestamp. This log is retained for
          security and compliance purposes and is not visible to customers or individual staff
          members.
        </p>

        <h3 style={styles.h3}>Historical ticket data</h3>
        <p style={styles.p}>
          When a ticket completes, is cancelled, skipped, or expires, an anonymized outcome row
          is written to the <code>historical_tickets</code> table (ticket identifier, shop,
          timestamps, wait time, outcome, source). After 30 days the ticket identifier is removed
          by an automated daily scrub job, leaving only non-identifying aggregate data. Aggregated
          analytics (for example daily roll-ups) may be retained indefinitely for service
          improvement.
        </p>

        <h3 style={styles.h3}>Review prompt</h3>
        <p style={styles.p}>
          After a ticket completes, customers may be shown a 1–5 star review prompt. <strong>We
          do not store any rating data.</strong> High ratings redirect the browser to an external
          review site chosen by the business (for example Google or Trustpilot); low ratings show
          a private thank-you screen. No data leaves your device other than the external redirect
          itself.
        </p>

        <h2 style={styles.h2}>2. How We Use Your Data</h2>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Purpose</th>
              <th style={styles.th}>Legal Basis (GDPR)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={styles.td}>Operate the queue service</td>
              <td style={styles.td}>Legitimate interest</td>
            </tr>
            <tr>
              <td style={styles.td}>Send push notifications about your position</td>
              <td style={styles.td}>Consent</td>
            </tr>
            <tr>
              <td style={styles.td}>Manage business accounts and billing</td>
              <td style={styles.td}>Contract performance</td>
            </tr>
            <tr>
              <td style={styles.td}>Generate anonymized analytics for businesses</td>
              <td style={styles.td}>Legitimate interest</td>
            </tr>
            <tr>
              <td style={styles.td}>Prevent abuse and ensure security</td>
              <td style={styles.td}>Legitimate interest</td>
            </tr>
          </tbody>
        </table>

        <h2 style={styles.h2}>3. Data Retention</h2>
        <ul style={styles.ul}>
          <li><strong>Queue session data</strong> (first name, avatar): deleted when your session ends, at the latest 24 hours after joining</li>
          <li><strong>Push subscriptions</strong>: deleted when the ticket is completed, cancelled, or the browser reports the subscription as expired</li>
          <li><strong>Historical tickets</strong>: anonymized 30 days after completion by an automated daily scrub (3 AM server time); audit of each scrub run is kept in <code>gdpr_scrub_audit</code></li>
          <li><strong>Email verification tokens</strong>: single-use, expire 24 hours after issue</li>
          <li><strong>Password reset tokens</strong>: single-use, expire 1 hour after issue</li>
          <li><strong>Employee invite tokens</strong>: single-use, expire 24 hours after issue unless revoked</li>
          <li><strong>Login sessions</strong>: retained for the lifetime of the account</li>
          <li><strong>Administrative audit log</strong>: retained for security and compliance purposes</li>
          <li><strong>Business accounts</strong>: retained while the account is active; deleted upon request</li>
          <li><strong>Payment records</strong>: retained as required by applicable tax and accounting laws</li>
        </ul>

        <h2 style={styles.h2}>4. Cookies and Local Storage</h2>
        <p style={styles.p}>
          We do not use tracking cookies or third-party analytics cookies. We use only:
        </p>
        <ul style={styles.ul}>
          <li><strong>JWT tokens</strong> stored in local storage for authentication (business users and kiosk sessions)</li>
          <li><strong>Session identifiers</strong> stored in local storage for queue participation and reconnection</li>
          <li><strong>UI preferences</strong> stored in local storage (for example the mute toggle on the waiting-room display screen)</li>
        </ul>
        <p style={styles.p}>
          No advertising trackers, no fingerprinting, no cross-site tracking.
        </p>
        <p style={styles.p}>
          Each of the items above is strictly necessary for the Service the
          user has requested — signing in, maintaining a queue session, or
          remembering an explicit UI preference. Under Article 5(3) of the
          ePrivacy Directive (and the national laws transposing it) storage
          that is strictly necessary for a service explicitly requested by
          the user is exempt from the prior-consent requirement, which is why
          we do not show a cookie banner. We do not read or write any
          non-essential storage.
        </p>

        <h2 style={styles.h2}>5. Third-Party Services and Sub-Processors</h2>
        <p style={styles.p}>
          We use the following sub-processors to operate the Service. Each is bound by a Data
          Processing Agreement and processes data only on our instructions. Where personal data
          leaves the European Economic Area, we rely on Standard Contractual Clauses approved
          by the European Commission.
        </p>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Sub-processor</th>
              <th style={styles.th}>Purpose</th>
              <th style={styles.th}>Region</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={styles.td}>
                <strong>Hetzner Online GmbH</strong><br />
                <a href="https://www.hetzner.com/legal/privacy-policy/" style={styles.link} target="_blank" rel="noopener noreferrer">Privacy Policy</a>
              </td>
              <td style={styles.td}>Application and proxy hosting (compute, network)</td>
              <td style={styles.td}>EU (Germany / Finland)</td>
            </tr>
            <tr>
              <td style={styles.td}>
                <strong>Neon, Inc.</strong><br />
                <a href="https://neon.tech/privacy-policy" style={styles.link} target="_blank" rel="noopener noreferrer">Privacy Policy</a>
              </td>
              <td style={styles.td}>Managed PostgreSQL — primary data store for accounts, configuration, and historical tickets</td>
              <td style={styles.td}>EU (Frankfurt)</td>
            </tr>
            <tr>
              <td style={styles.td}>
                <strong>Upstash, Inc.</strong><br />
                <a href="https://upstash.com/trust/privacy.pdf" style={styles.link} target="_blank" rel="noopener noreferrer">Privacy Policy</a>
              </td>
              <td style={styles.td}>Managed Redis — short-lived queue state, refresh tokens, rate limits, ticket sequences</td>
              <td style={styles.td}>EU</td>
            </tr>
            <tr>
              <td style={styles.td}>
                <strong>Resend, Inc.</strong><br />
                <a href="https://resend.com/legal/privacy-policy" style={styles.link} target="_blank" rel="noopener noreferrer">Privacy Policy</a>
              </td>
              <td style={styles.td}>Transactional email delivery (sign-up verification, password reset, staff invites)</td>
              <td style={styles.td}>EU / US</td>
            </tr>
            <tr>
              <td style={styles.td}>
                <strong>Stripe Payments Europe Ltd.</strong><br />
                <a href="https://stripe.com/privacy" style={styles.link} target="_blank" rel="noopener noreferrer">Privacy Policy</a>
              </td>
              <td style={styles.td}>Payment processing and subscription billing. We never see card numbers.</td>
              <td style={styles.td}>Ireland</td>
            </tr>
            <tr>
              <td style={styles.td}>
                <strong>Cloudflare, Inc.</strong><br />
                <a href="https://www.cloudflare.com/privacypolicy/" style={styles.link} target="_blank" rel="noopener noreferrer">Privacy Policy</a>
              </td>
              <td style={styles.td}>Turnstile bot-protection challenge shown when a customer joins a queue</td>
              <td style={styles.td}>US / global</td>
            </tr>
            <tr>
              <td style={styles.td}>
                <strong>Functional Software, Inc. (Sentry)</strong><br />
                <a href="https://sentry.io/privacy/" style={styles.link} target="_blank" rel="noopener noreferrer">Privacy Policy</a>
              </td>
              <td style={styles.td}>
                Application error monitoring. We strip IP addresses, email addresses, request
                bodies, cookies, and authentication headers before any event leaves our servers,
                so Sentry only receives stack traces and non-identifying technical metadata.
              </td>
              <td style={styles.td}>EU</td>
            </tr>
            <tr>
              <td style={styles.td}>
                <strong>Browser push services</strong><br />
                (Mozilla autopush, Google FCM, Apple APNs)
              </td>
              <td style={styles.td}>
                Routes encrypted Web Push payloads to your device. Payload content is encrypted
                with keys generated in your browser; the push service cannot read it.
              </td>
              <td style={styles.td}>US</td>
            </tr>
          </tbody>
        </table>
        <p style={styles.p}>
          We do not use advertising networks, marketing trackers, or third-party analytics
          providers. We will update this list at least 30 days before adding a new
          sub-processor that processes personal data.
        </p>

        <h2 style={styles.h2}>6. Your Rights (GDPR)</h2>
        <p style={styles.p}>If you are in the European Economic Area, you have the right to:</p>
        <ul style={styles.ul}>
          <li><strong>Access</strong> -- request a copy of the personal data we hold about you</li>
          <li><strong>Rectification</strong> -- request correction of inaccurate data</li>
          <li><strong>Erasure</strong> -- request deletion of your personal data ("right to be forgotten")</li>
          <li><strong>Portability</strong> -- receive your data in a structured, machine-readable format</li>
          <li><strong>Restriction</strong> -- request that we limit processing of your data</li>
          <li><strong>Objection</strong> -- object to processing based on legitimate interest</li>
          <li><strong>Withdraw consent</strong> -- for push notifications, you can withdraw consent at any time by disabling notifications in your browser</li>
        </ul>
        <p style={styles.p}>
          To exercise any of these rights, contact us
          at <a href="mailto:privacy@waitless.live" style={styles.link}>privacy@waitless.live</a>.
          We will respond within 30 days.
        </p>
        <p style={styles.p}>
          Signed-in business users can additionally self-serve access and erasure:
          a full JSON export of the personal data we hold on your account is available
          at <code>GET /api/v1/users/me/export</code>, and an authenticated staff or
          manager account can be erased at <code>DELETE /api/v1/users/me</code>. Workspace
          owners must instead email us so we can safely close the tenant alongside all
          its shops, employees, and billing records.
        </p>

        <h2 style={styles.h2}>7. Data Security</h2>
        <p style={styles.p}>
          We implement appropriate technical and organizational measures to protect your data,
          including encrypted connections (TLS), hashed passwords, tenant-isolated data access,
          and regular security reviews.
        </p>

        <h2 style={styles.h2}>8. Children's Privacy</h2>
        <p style={styles.p}>
          The Service is not directed at children under 16. We do not knowingly collect personal
          data from children. If you believe a child has provided us with personal data, please
          contact us and we will delete it promptly.
        </p>

        <h2 style={styles.h2}>9. International Data Transfers</h2>
        <p style={styles.p}>
          Your data may be processed in countries outside the EEA. Where this occurs, we ensure
          appropriate safeguards are in place, such as Standard Contractual Clauses approved by
          the European Commission.
        </p>

        <h2 style={styles.h2}>10. Changes to This Policy</h2>
        <p style={styles.p}>
          We may update this Privacy Policy from time to time. We will notify registered business
          users of material changes via email. The "Last updated" date at the top indicates the
          most recent revision.
        </p>

        <h2 style={styles.h2}>11. Contact</h2>
        <p style={styles.p}>
          For privacy-related inquiries, contact us
          at <a href="mailto:privacy@waitless.live" style={styles.link}>privacy@waitless.live</a>.
        </p>

        <p style={{ ...styles.p, marginTop: '2rem', borderTop: '1px solid #1E293B', paddingTop: '1.5rem' }}>
          See also our <Link to="/terms" style={styles.link}>Terms of Service</Link>.
        </p>
      </div>
    </div>
  );
}
